import config from '../config/config';

/**
 * API Service for backend integration
 */
class ApiService {
  /**
   * Create user profile
   * POST /api/v1/profile/
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @returns {Promise<Object>} API response containing created profile
   */
  async createProfile(jwtToken) {
    const endpoint = config.getProfileEndpoint();

    try {
      console.log('📤 Creating profile in API:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Create profile API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Profile created:', result);
      return result;

    } catch (error) {
      console.error('❌ Error creating profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile
   * GET /api/v1/profile/
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @returns {Promise<Object>} API response containing user profile
   */
  async getProfile(jwtToken) {
    const endpoint = config.getProfileEndpoint();

    try {
      console.log('📤 Fetching profile from API:', endpoint);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Profile API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Profile fetched:', result);
      return result;

    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile attribute
   * PATCH /api/v1/profile/attribute
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @param {string} attribute - Attribute name to update (e.g., "email")
   * @param {string} newValue - New value for the attribute
   * @returns {Promise<Object>} API response
   */
  async updateProfileAttribute(jwtToken, attribute, newValue) {
    const endpoint = config.getProfileAttributeEndpoint();

    try {
      console.log('📤 Updating profile attribute:', attribute);

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          attribute: attribute,
          new_value: newValue
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Update attribute API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Profile attribute updated:', result);
      return result;

    } catch (error) {
      console.error('❌ Error updating profile attribute:', error);
      throw error;
    }
  }

  /**
   * Create a new session
   * POST /api/v1/session/
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @param {Object} sessionData - Session data to create
   * @param {string} sessionData.session_id - Firestore session ID
   * @param {string} sessionData.session_name - Name of the session
   * @param {string} sessionData.session_type - Type of session (e.g., "quiz")
   * @param {string} sessionData.chosen_ai - AI character chosen
   * @param {string} sessionData.chosen_ai_role - Role of the AI
   * @param {string} sessionData.session_goal - Goal of the session
   * @returns {Promise<Object>} API response containing backend session_id
   */
  async createSession(jwtToken, sessionData) {
    const endpoint = config.getSessionEndpoint();

    try {
      console.log('📤 Sending session to API:', endpoint);
      console.log('Session data:', sessionData);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Session API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Session created:', result);
      return result;

    } catch (error) {
      console.error('❌ Error creating session:', error);
      throw error;
    }
  }

  /**
   * Send chat messages with quiz data
   * POST /api/v1/chat/?include_analysis=true
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @param {Object} chatData - Chat data to send
   * @param {string} chatData.message - The message text
   * @param {string} chatData.session_id - Backend session ID (from createSession response)
   * @param {string} chatData.senderId - User ID
   * @param {Object} chatData.quiz_data - Quiz questions and answers
   * @param {Array<string>} chatData.quiz_data.questions - Array of question texts
   * @param {Array<Array>} chatData.quiz_data.answers - Array of arrays containing answers for each question
   * @returns {Promise<Object>} API response
   */
  async sendChat(jwtToken, chatData) {
    const endpoint = `${config.getChatEndpoint()}?include_analysis=true`;

    try {
      console.log('📤 Sending chat to API:', endpoint);
      console.log('Chat data:', chatData);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(chatData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chat API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Chat sent:', result);
      return result;

    } catch (error) {
      console.error('❌ Error sending chat:', error);
      throw error;
    }
  }

  /**
   * Submit complete quiz results
   * This method:
   * 1. Creates the session in backend
   * 2. Gathers all quiz questions and answers from Firestore
   * 3. Sends quiz data to chat endpoint
   *
   * @param {string} jwtToken - Firebase JWT token for authentication
   * @param {string} firestoreSessionId - Firestore session ID
   * @param {string} userId - Firebase user UID
   * @param {string} userName - User's display name
   * @param {Array} questions - Array of all question objects
   * @param {Array} chatMessages - Array of chat messages from Firestore
   * @returns {Promise<Object>} Results containing session and chat responses
   */
  async submitQuizResults(jwtToken, firestoreSessionId, userId, userName, questions, chatMessages) {
    try {
      console.log('🚀 Submitting quiz results to API...');

      // Step 1: Create session in backend
      const sessionPayload = {
        session_id: firestoreSessionId,
        session_name: `${userName}'s Personality Quiz`,
        session_type: "quiz",
        chosen_ai: "1",
        chosen_ai_role: "personality_coach",
        session_goal: "Discover personality insights and relationship needs"
      };

      const sessionResult = await this.createSession(jwtToken, sessionPayload);

      console.log('✅ Backend session created');

      // Step 2: Extract questions and answers from chat messages
      const quizQuestions = [];
      const quizAnswers = [];

      // Group messages by question order
      const questionMap = new Map();

      chatMessages.forEach(msg => {
        if (msg.questionData && msg.questionData.order) {
          const order = msg.questionData.order;

          if (!questionMap.has(order)) {
            questionMap.set(order, {
              question: null,
              answers: []
            });
          }

          if (msg.type === 'user') {
            // This is a user answer
            const question = questions.find(q => q.order === order);
            if (question && !questionMap.get(order).question) {
              questionMap.get(order).question = question.question_text;
            }

            // Add the answer - always use msg.message which contains the actual text
            // For multi-select, msg.message is comma-separated, so split it
            const answerText = msg.message;
            if (answerText.includes(',')) {
              // Multi-select answer: "option1, option2, option3"
              questionMap.get(order).answers = answerText.split(', ').map(s => s.trim());
            } else {
              // Single answer
              questionMap.get(order).answers = [answerText];
            }
          }
        }
      });

      // Sort by question order and build arrays
      const sortedOrders = Array.from(questionMap.keys()).sort((a, b) => a - b);
      sortedOrders.forEach(order => {
        const data = questionMap.get(order);
        if (data.question) {
          quizQuestions.push(data.question);
          quizAnswers.push(data.answers);
        }
      });

      console.log('📝 Extracted quiz data:', {
        questions: quizQuestions.length,
        answers: quizAnswers.length
      });
      console.log('📋 Full quiz_data structure:', {
        questions: quizQuestions,
        answers: quizAnswers
      });

      // Step 3: Send quiz data to chat endpoint
      const chatPayload = {
        message: `Quiz completed by ${userName}`,
        link: "",
        session_id: firestoreSessionId, // Use Firestore session ID
        senderId: userId,
        messageType: "text",
        key: "chat_message",
        message_id: `${firestoreSessionId}_completion`,
        type: "human",
        j_role: "personality_coach",
        topic: null,
        practice_data: null,
        quiz_data: {
          questions: quizQuestions,
          answers: quizAnswers
        }
      };

      const chatResult = await this.sendChat(jwtToken, chatPayload);

      console.log('✅ Quiz data submitted successfully!');

      return {
        session: sessionResult,
        chat: chatResult,
        sessionId: firestoreSessionId
      };

    } catch (error) {
      console.error('❌ Error submitting quiz results:', error);
      throw error;
    }
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
